import React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { CallHistoryMethodAction, push } from 'connected-react-router';

import { Translate } from 'react-localize-redux';

import { rgba } from 'polished';
import styled from 'styled-components';

// Store
import AppState from '@webapp/store/state';
import { CreateOrder } from '@webapp/store/orders/action-creators';

// Services
import { Price } from '@common/types/price/price';
import { Product } from '@services/product/types/product';

// Types
import { Order } from '@webapp/types/orders/order';
import { Release } from '@common/types/release/release';

import Box from '@components/layout/box';
import Flex from '@components/layout/flex';
import Chip from '@components/basics/chip';
import Button from '@components/actions/button';
import BackIcon from '@components/basics/icons/back';
import Typography from '@components/basics/typography';
import UnselectedIcon from '@components/basics/icons/unselected';
import CompletedIcon from '@components/basics/icons/achievements-icons/completed';
import RightArrow from '@components/basics/icons/right-arrow';
import ButtonFixed from '@components/layout/button-fixed';
import { getWidth } from '@components/styles';
import { releaseCheckout } from '@webapp/store/release/selectors';

type PlanType = 'pay' | 'upgrade' | '';

interface PlanUpgradePropmtPageProps {
  className?: string;
  upgradeFeatures?: string[];
  basket: Order[];
  release?: Partial<Release>;
  proPlan: Product;
  isMobile: boolean;
  isTablet: boolean;
  publish: (artistId: string, releaseId: string) => CallHistoryMethodAction;
  goBack: (artistId: string, releaseId: string) => CallHistoryMethodAction;
  selectPlan: (product: Product, price: Price) => void;
  goToPayment: (clientId: string, id: string) => CallHistoryMethodAction;
}

const mapStateToProps = (state: AppState) => ({
  upgradeFeatures:
    state.products.list.find((p) => p.product_key === 'plan_pro')
      ?.upgradeFeatures || [],
  proPlan: state.products.list?.find(
    (product) =>
      product.product_key === 'plan_trac_pro' && product.category === 'PLAN'
  ),
  basket: releaseCheckout(state),
  release: state.release.release,
  isMobile: state.ui.mobile,
  isTablet: state.ui.tablet,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  publish: (artistId: string, releaseId: string) =>
    dispatch(push(`/release/${artistId}/${releaseId}/checkout/distribution`)),
  goToPayment: (clientId: string, id: string) =>
    dispatch(
      push(
        `/payment/${clientId}/${
          id || ''
        }?redirectTo=/release/${clientId}/${id}/payment-success`
      )
    ),
  selectPlan: (product: Product, price: Price) =>
    dispatch(
      new CreateOrder({
        product,
        price,
        quantity: 1,
      })
    ),
  goBack: (artistId: string, releaseId: string) =>
    dispatch(push(`/release/${artistId}/${releaseId}/preview`)),
});

export const PlanUpgradePromptPage: React.FC<PlanUpgradePropmtPageProps> = ({
  ...props
}) => {
  const match = useRouteMatch<{
    artistId: string;
    releaseId: string;
  }>();
  const [selectType, setSelectType] = React.useState<PlanType>('');

  const handleCheckout = () => {
    if (selectType === 'pay') {
      props.publish(match.params.artistId, match.params.releaseId);
      return;
    }
    if (selectType === 'upgrade' && props.proPlan) {
      const price = props.proPlan.prices.find((p) => p.frequency === 'year');
      if (!price) {
        return;
      }
      handleSelection(props.proPlan, price);
    }
  };

  const handleSelection = (product: Product, price: Price) => {
    props.selectPlan(product, price);

    return props.goToPayment(match.params.artistId, match.params.releaseId);
  };

  const Plans = [
    {
      id: 'pay' as PlanType,
      title: 'PAYMENT.PLAN_UPGRADE.PAY.TITLE',
      tag: 'PAYMENT.PLAN_UPGRADE.PAY.TAG',
      text: props.basket?.map((basket) => basket.product.name).join(' • '),
      price:
        props.basket?.reduce((t, o) => t + o.price.value * o.quantity, 0) / 100,
      priceText: undefined,
      hidePrice: false,
      isBlue: false,
    },
    {
      id: 'upgrade' as PlanType,
      title: 'PAYMENT.PLAN_UPGRADE.UPGRADE.TITLE',
      tag: 'PAYMENT.PLAN_UPGRADE.UPGRADE.TAG',
      text: (
        <Translate
          id="PAYMENT.PLAN_UPGRADE.UPGRADE.TEXT"
          data={{
            amount: `$${(
              props.basket?.reduce(
                (t, o) => t + o.price.value * o.quantity,
                0
              ) / 100
            ).toFixed(0)}`,
          }}
        />
      ),
      price: (
        (props.proPlan.prices.find((p) => p.frequency === 'year')?.value || 0) /
        100
      ).toFixed(0),
      priceText: 'PAYMENT.PLAN_UPGRADE.UPGRADE.ANNUALY',
      hidePrice: true,
      isBlue: true,
    },
  ];

  return (
    <div className={props.className}>
      <PositionedBackIcon
        onClick={() =>
          props.goBack(match.params.artistId, match.params.releaseId)
        }
      />

      <Typography className="mb-30" type="subheader" customFont="semi-bold">
        <Translate id="PAYMENT.TITLE" />
      </Typography>

      <Wrapper>
        {Plans.map((plan, i) => (
          <Box
            key={`${plan.id}-${i}`}
            className={`plan-box ${
              plan.isBlue && !(selectType === plan.id) ? 'blue' : ''
            } ${selectType === plan.id ? 'active' : ''}`}
            noBorderBottom
            noBorderTop={i !== 0}
          >
            <Flex justifyContent="space-between" alignItems="center" gap={20}>
              <div>
                <Flex justifyContent="flex-start" alignItems="center">
                  <Typography className="title" weight="700">
                    <Translate id={plan.title} />
                  </Typography>
                  <Typography
                    className={`tag ${plan.isBlue ? 'white' : ''} `}
                    type="small"
                    weight="700"
                  >
                    <Translate id={plan.tag} />
                  </Typography>
                </Flex>
                <Typography className="text" type="small">
                  {plan.text}
                </Typography>
              </div>
              <Flex justifyContent="flex-end" alignItems="center" gap={10}>
                {plan.price && !plan.hidePrice ? (
                  <Chip
                    chipType="success"
                    hasActive={selectType === plan.id}
                    small
                  >
                    ${plan.price}
                  </Chip>
                ) : plan.hidePrice && selectType === plan.id ? (
                  <div className="chip-w-text">
                    <Chip
                      chipType="success"
                      hasActive={selectType === plan.id}
                      small
                    >
                      ${plan.price}
                    </Chip>
                    <SmallText className="below-chip">
                      <Translate id={plan.priceText} />
                    </SmallText>
                  </div>
                ) : (
                  <Spacer />
                )}
                {selectType === plan.id ? (
                  <CompletedIcon
                    className="icon"
                    onClick={() => setSelectType('')}
                  />
                ) : (
                  <UnselectedIcon
                    className="icon"
                    isWhite={plan.isBlue}
                    active={plan.isBlue}
                    onClick={() => setSelectType(plan.id)}
                  />
                )}
              </Flex>
            </Flex>
          </Box>
        ))}
        <BlueHint className={selectType === 'upgrade' ? 'is-active' : ''}>
          <Typography className="blue-hint-text" type="small" weight="700">
            <Translate id="PAYMENT.PLAN_UPGRADE.UPGRADE.HINT" />
          </Typography>
        </BlueHint>
      </Wrapper>

      {props.isTablet || props.isMobile ? (
        <CheckoutButton
          onClick={handleCheckout}
          disabled={!selectType}
          customContent={
            <Flex justifyContent="space-between" alignItems="center">
              <Typography weight="700">
                <Translate id="PAYMENT.PLAN_UPGRADE.BUTTON" />
              </Typography>
              <RightArrow disabled={!selectType} isWhite={!!selectType} />
            </Flex>
          }
        />
      ) : (
        <ButtonFixed>
          <CheckoutButton
            onClick={handleCheckout}
            disabled={!selectType}
            customContent={
              <Flex justifyContent="space-between" alignItems="center">
                <Typography weight="700">
                  <Translate id="PAYMENT.PLAN_UPGRADE.BUTTON" />
                </Typography>
                <RightArrow disabled={!selectType} isWhite={!!selectType} />
              </Flex>
            }
          />
        </ButtonFixed>
      )}
    </div>
  );
};
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(PlanUpgradePromptPage)``);

const PositionedBackIcon = styled(BackIcon)`
  margin-bottom: 20px;
  @media ${({ theme }) => theme.device.tablet} {
    margin-bottom: 0;
    position: absolute;
    top: 0;
    left: 0;
  }
`;

const Wrapper = styled.div`
  display: grid;
  grid-gap: 3px;

  .plan-box {
    transition: background-color
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};

    &.blue {
      background-color: ${({ theme }) => theme.colors.blueColor};
      .title {
        color: ${({ theme }) => theme.colors.white};
      }
      .text {
        color: ${({ theme }) => rgba(theme.colors.white, 0.5)};
      }
    }

    &.active {
      background-color: ${({ theme }) => theme.colors.primary};
      .title {
        color: ${({ theme }) => theme.colors.differentBackground};
      }
      .text {
        color: ${({ theme }) => rgba(theme.colors.differentBackground, 0.5)};
      }
    }

    .title,
    .text {
      transition: color
        ${({ theme }) =>
          `${theme.transitions.duration} ${theme.transitions.type}`};
    }

    .icon {
      width: 32px;
      height: 32px;
    }

    .tag {
      padding: 2px 8px;
      margin-left: 5px;
      border-radius: 90px;
      line-height: 15px;
      color: ${({ theme }) => theme.colors.black};
      background-color: ${({ theme }) => theme.colors.hint};
      transition: background-color
        ${({ theme }) =>
          `${theme.transitions.duration} ${theme.transitions.type}`};

      &.white {
        color: ${({ theme }) => theme.colors.blueColor};
        background-color: ${({ theme }) => theme.colors.white};
      }
    }

    .text {
      margin-top: 5px;
      color: ${({ theme }) => theme.colors.tertiary};
    }

    .chip-w-text {
      position: relative;
      .below-chip {
        position: absolute;
        top: calc(100% + 3px);
        left: 50%;
        transform: translate(-50%, 0);
      }
    }
  }
`;

const SmallText = styled(Typography)`
  text-align: center;
  font-size: ${({ theme }) => theme.typography.small};
  color: ${({ theme }) => theme.colors.tertiary};
`;

const Spacer = styled.div`
  width: 48px;
  height: 48px;
  background: transparent;
  pointer-events: none;
  user-select: none;
`;

const BlueHint = styled.div`
  padding: 10px 30px;
  border-radius: 0 0 10px 10px;
  background-color: ${({ theme }) => theme.colors.blueColor};
  transition: background-color
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`},
    color
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};

  .blue-hint-text {
    color: ${({ theme }) => theme.colors.white};
  }

  &.is-active {
    background-color: ${({ theme }) => theme.colors.primary};
    .blue-hint-text {
      color: ${({ theme }) => theme.colors.differentBackground};
    }
  }
`;

const CheckoutButton = styled(Button)`
  width: 100%;
  height: 77px;
  max-height: none;
  margin-top: 20px;

  @media ${({ theme }) => theme.device.laptopS} {
    margin-top: 0;
    max-width: ${getWidth(5)} !important;
  }
`;
